/**
 * Pick a company logo from disk.
 *
 * Used in two places that need slightly different behaviour, so it works in two
 * modes depending on whether it is given a company:
 *
 *   with companyID    the file is uploaded straight away and the company is
 *                     updated — the settings screens
 *   without companyID the file is only held and previewed, and the parent
 *                     uploads it once the company exists — the create form
 *
 * The preview is a local object URL, not the stored one, so the picked image
 * shows before any round trip.
 */

import React, { useEffect, useRef, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Avatar, Button, Typography } from "@material-ui/core";
import { uploadCompanyLogo } from "../modules/adminApi";
import { resolveLogoUrl } from "../modules/accountLogos";
import { DANGER, MUTED } from "./accountSettingsComponents/settingsPalette";

const ACCEPTED = "image/png,image/jpeg,image/webp,image/gif";
const MAX_BYTES = 2 * 1024 * 1024;

const useStyles = makeStyles(() => ({
  root: { display: "flex", alignItems: "center", gap: "12px" },
  avatar: { width: "48px", height: "48px" },
  controls: { display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "2px" },
  hint: { fontSize: "0.7rem", color: MUTED },
  failure: { fontSize: "0.7rem", color: DANGER },
  hiddenInput: { display: "none" },
}));

export default function CompanyLogoPicker(props) {
  const classes = useStyles();
  const inputRef = useRef(null);

  const [previewUrl, setPreviewUrl] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [failure, setFailure] = useState(null);

  // An object URL is a handle on a blob; it stays alive until it is revoked.
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  // The create form clears itself after a company is made; drop the preview too.
  useEffect(() => {
    if (props.resetToken === undefined) return;
    setPreviewUrl(null);
    setFailure(null);
  }, [props.resetToken]);

  const shownUrl = previewUrl || resolveLogoUrl(props.logoUrl);

  async function onFilePicked(event) {
    const file = event.target.files && event.target.files[0];
    // Let the same file be picked again after a failure.
    event.target.value = "";
    if (!file) return;

    setFailure(null);
    if (file.size > MAX_BYTES) {
      setFailure("That image is over 2MB. Pick a smaller one.");
      return;
    }
    if (ACCEPTED.split(",").indexOf(file.type) === -1) {
      setFailure("PNG, JPEG, WEBP or GIF only.");
      return;
    }

    setPreviewUrl(URL.createObjectURL(file));

    if (!props.companyID) {
      // Create form: the company does not exist yet, so it is uploaded after.
      props.onFileSelected?.(file);
      return;
    }

    setIsUploading(true);
    const result = await uploadCompanyLogo(props.companyID, file);
    setIsUploading(false);

    if (!result.ok) {
      setFailure(String(result.data || result.status));
      props.createSnackbar?.("Could not upload the logo.", "error", false);
      return;
    }
    props.createSnackbar?.("Logo updated.", "success", false);
    props.onUploaded?.(result.data.logoUrl);
  }

  return (
    <div className={classes.root}>
      <Avatar className={classes.avatar} src={shownUrl || undefined} alt="" />
      <div className={classes.controls}>
        <input
          id={props.inputId || "companyLogoInput"}
          ref={inputRef}
          className={classes.hiddenInput}
          type="file"
          accept={ACCEPTED}
          onChange={onFilePicked}
        />
        <Button size="small" disabled={isUploading} onClick={() => inputRef.current && inputRef.current.click()}>
          {isUploading ? "Uploading…" : props.buttonLabel || "Change logo"}
        </Button>
        {failure ? (
          <Typography className={classes.failure}>{failure}</Typography>
        ) : (
          <Typography className={classes.hint}>PNG, JPEG, WEBP or GIF · up to 2MB</Typography>
        )}
      </div>
    </div>
  );
}
